/**
 * BeezFy Lookbook Mosaic JavaScript
 * Gestione interazioni, hotspot e integrazione con sistemi BeezFy
 * Compatible with BeezFy centralized systems architecture
 * Last update: 2025-10-04 - PREMIUM TOUCH SYSTEM 2025
 */

(function() {
  // Evita ridichiarazioni multiple nel Theme Editor
  if (window.BeezfyLookbookMosaic) {
    console.log('[Lookbook Mosaic] Already loaded, skipping');
    return;
  }

/**
 * TOOLTIP MANAGER GLOBALE - SINGLETON
 * Gestisce TUTTI i tooltip su TUTTA la pagina
 * PREVIENE apertura multipla garantendo un solo tooltip aperto alla volta
 */
class TooltipManager {
  constructor() {
    if (TooltipManager.instance) {
      return TooltipManager.instance;
    }

    this.currentOpenTooltip = null; // Tooltip correntemente aperto
    this.currentHotspot = null;     // Hotspot correntemente attivo
    TooltipManager.instance = this;
  }

  /**
   * Apre un tooltip e CHIUDE TUTTI GLI ALTRI
   * BFY-LBM: Auto-close dopo 3 secondi senza interazione
   */
  openTooltip(hotspot, tooltip, isMobile = false) {
    // Se è lo stesso tooltip già aperto, non fare nulla
    if (this.currentOpenTooltip === tooltip && this.currentHotspot === hotspot) {
      return false; // Indica che era già aperto
    }

    // CHIUDI TUTTI i tooltip aperti ovunque nella pagina
    this.closeAll(isMobile);

    // Cancella eventuali timer precedenti
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    // Apri il nuovo tooltip
    tooltip.style.opacity = '1';
    tooltip.style.visibility = 'visible';
    tooltip.style.pointerEvents = 'auto';
    tooltip.style.transform = isMobile
      ? 'translate(-50%, -50%) scale(1)'
      : 'translateX(-50%) scale(1)';

    // Visual feedback su mobile
    if (isMobile) {
      hotspot.classList.add('bzfy-lkbk-active');
      hotspot.style.transform = 'scale(1.15)';
      setTimeout(() => {
        hotspot.style.transform = 'scale(1)';
      }, 200);
    }

    // Salva riferimento
    this.currentOpenTooltip = tooltip;
    this.currentHotspot = hotspot;

    // BFY-LBM: Auto-close dopo 3 secondi senza interazione
    this.autoCloseTimer = setTimeout(() => {
      this.closeTooltip(hotspot, tooltip, isMobile);
    }, 3000);

    return true; // Indica che è stato aperto
  }

  /**
   * Chiude un tooltip specifico
   */
  closeTooltip(hotspot, tooltip, isMobile = false) {
    if (!tooltip) return;

    // BFY-LBM: Cancella auto-close timer
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    tooltip.style.opacity = '0';
    tooltip.style.visibility = 'hidden';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.transform = isMobile
      ? 'translate(-50%, -50%) scale(0.92)'
      : 'translateX(-50%) scale(0.92)';

    if (isMobile && hotspot) {
      hotspot.classList.remove('bzfy-lkbk-active');
    }

    // Se è il tooltip corrente, resetta
    if (this.currentOpenTooltip === tooltip) {
      this.currentOpenTooltip = null;
      this.currentHotspot = null;
    }
  }

  /**
   * BFY-LBM: Cancella auto-close timer (chiamato quando utente interagisce)
   */
  cancelAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  /**
   * Chiude TUTTI i tooltip in TUTTA la pagina
   */
  closeAll(isMobile = false) {
    // Trova TUTTI i tooltip aperti in TUTTE le sezioni
    const allOpenTooltips = document.querySelectorAll('.lookbook-hotspot__tooltip[style*="visibility: visible"]');
    const allActiveHotspots = document.querySelectorAll('.lookbook-hotspot.bzfy-lkbk-active');

    allOpenTooltips.forEach(tooltip => {
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
      tooltip.style.pointerEvents = 'none';
      tooltip.style.transform = isMobile
        ? 'translate(-50%, -50%) scale(0.92)'
        : 'translateX(-50%) scale(0.92)';
    });

    allActiveHotspots.forEach(hotspot => {
      hotspot.classList.remove('bzfy-lkbk-active');
    });

    this.currentOpenTooltip = null;
    this.currentHotspot = null;
  }

  /**
   * Verifica se un tooltip è attualmente aperto
   */
  isOpen(tooltip) {
    return this.currentOpenTooltip === tooltip;
  }
}

// Crea istanza globale singleton
window.BeezfyTooltipManager = new TooltipManager();

class BeezfyLookbookMosaic {
  constructor() {
    this.sections = [];
    this.settings = window.beezfySettings || {};
    this.isDebug = window.location.search.includes('debug=true');

    this.bindEvents();
    this.init();
  }

  /**
   * Inizializzazione sistema
   */
  init() {
    // Aspetta che tutti i sistemi BeezFy siano caricati
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initSections());
    } else {
      this.initSections();
    }
  }

  /**
   * Inizializza tutte le sezioni lookbook
   */
  initSections() {
    const lookbookSections = document.querySelectorAll('.beezfy-lookbook-mosaic');

    lookbookSections.forEach(section => {
      const sectionInstance = new LookbookSection(section, this.settings);
      this.sections.push(sectionInstance);
    });

    this.log('Initialized', this.sections.length, 'lookbook sections');
  }

  /**
   * Bind eventi globali
   */
  bindEvents() {
    // Resize handler per responsive layouts
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.sections.forEach(section => section.handleResize());
      }, 250);
    });

    // Intersection Observer per lazy loading e analytics
    this.setupIntersectionObserver();

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.sections.forEach(section => section.destroy());
    });
  }

  /**
   * Setup Intersection Observer per performance e analytics
   */
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          const sectionInstance = this.sections.find(s => s.element === section);

          if (sectionInstance && !sectionInstance.hasBeenViewed) {
            sectionInstance.handleFirstView();
          }
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.1
    });
  }

  /**
   * Logging helper
   */
  log(...args) {
    if (this.isDebug) {
    }
  }
}

/**
 * Singola sezione Lookbook
 */
class LookbookSection {
  constructor(element, globalSettings) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.layoutType = element.dataset.sectionType;
    this.settings = globalSettings;
    this.hasBeenViewed = false;
    this.campaignTag = element.dataset.campaign;
    this.trackingEnabled = this.element.querySelector('[data-analytics-tile]') !== null;

    // BFY-LBM: Bound listeners object (Theme Editor safe)
    this.boundListeners = {};

    // BFY-LBM: Media query reattiva con fallback Safari
    this.mq = window.matchMedia('(max-width: 749px)');
    this.isMobile = this.mq.matches;

    // Listen for MQ changes (fallback per browser vecchi)
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', (e) => {
        this.isMobile = e.matches;
      });
    } else {
      this.mq.addListener((e) => {
        this.isMobile = e.matches;
      });
    }

    // Elementi del DOM
    this.grid = element.querySelector('.lookbook-grid');
    this.tiles = Array.from(element.querySelectorAll('.lookbook-tile'));
    this.hotspots = Array.from(element.querySelectorAll('.lookbook-hotspot'));

    // Usa il TooltipManager globale singleton
    this.tooltipManager = window.BeezfyTooltipManager;

    this.init();
  }

  /**
   * Inizializzazione sezione
   */
  init() {
    this.setupTiles();
    this.setupHotspots();
    this.setupCarousel();
    this.setupVideos();
    this.setupLightbox();
    this.setupAccessibility();

    // BFY-LBM: Bind global listeners (Theme Editor safe)
    this.bindGlobalListeners();

    // BFY-LBM: Interaction hardening (mobile UX)
    this.setupInteractionHardening();

    // Analytics setup
    if (this.trackingEnabled) {
      this.setupAnalytics();
    }

    // Add to global observer
    if (window.beezfyLookbook?.observer) {
      window.beezfyLookbook.observer.observe(this.element);
    }

    this.log('Section initialized:', this.sectionId);
  }

  /**
   * BFY-LBM: Bind global listeners con cleanup per Theme Editor
   */
  bindGlobalListeners() {
    // ESC key global (con guardia anti-duplicazione)
    if (!window.__bfyLbmEscBound) {
      window.__bfyLbmEscBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          window.BeezfyTooltipManager?.closeAll?.(
            window.matchMedia('(max-width: 749px)').matches
          );
        }
      });
    }

    // Click-outside (scope alla sezione, con riferimento salvato per unbind)
    this.boundListeners.onDocClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.tooltipManager.closeAll(this.isMobile);
      }
    };
    document.addEventListener('click', this.boundListeners.onDocClick, true);
  }

  /**
   * Setup tiles base
   */
  setupTiles() {
    this.tiles.forEach((tile, index) => {
      const tileType = tile.dataset.tileType;
      const tileLink = tile.querySelector('.lookbook-tile__link');

      // Click tracking per tile
      if (tileLink && this.trackingEnabled) {
        tileLink.addEventListener('click', (e) => {
          this.trackEvent('tile_click', {
            tile_index: index + 1,
            tile_type: tileType,
            tile_id: tile.dataset.tileId,
            url: tileLink.href
          });
        });
      }

      // Lazy loading per immagini
      this.setupLazyLoading(tile);
    });
  }

  /**
   * Setup hotspot system integrato con BeezFy
   */
  setupHotspots() {
    this.hotspots.forEach((hotspot, index) => {
      const productId = hotspot.dataset.productId;
      const variantId = hotspot.dataset.variantId;
      const productHandle = hotspot.dataset.productHandle;

      // BFY-LBM: Tooltip show/hide con ARIA e keyboard handling
      this.setupHotspotTooltip(hotspot);
    });
  }

  /**
   * BFY-LBM: Gestione tooltip hotspot - PREMIUM TOUCH SYSTEM 2025
   * - 44px minimum touch target
   * - Visual feedback on interaction
   * - Prevent accidental taps
   * - Single tooltip open at a time (GLOBAL MANAGER)
   * - ARIA sync + Theme Editor safe
   */
  setupHotspotTooltip(hotspot) {
    const trigger = hotspot.querySelector('.bfy-lbm-hotspot-trigger');
    const tooltip = hotspot.querySelector('.lookbook-hotspot__tooltip');
    if (!trigger || !tooltip) return;

    let hideTimeout;

    const showTooltip = () => {
      clearTimeout(hideTimeout);

      setTimeout(() => {
        const wasOpened = this.tooltipManager.openTooltip(hotspot, tooltip, this.isMobile);

        if (wasOpened) {
          // BFY-LBM: Smart positioning to prevent viewport overflow (desktop only)
          if (!this.isMobile && window.innerWidth >= 750) {
            const tileRect = hotspot.closest('.lookbook-tile')?.getBoundingClientRect();
            const hotspotRect = hotspot.getBoundingClientRect();

            if (tileRect && hotspotRect) {
              const tooltipWidth = 280;
              const tooltipHeight = 150;
              const viewportWidth = window.innerWidth;
              const viewportHeight = window.innerHeight;

              let position = [];

              // Check vertical position (top vs bottom)
              if (hotspotRect.top < tooltipHeight + 50) {
                position.push('bottom');
              }

              // Check horizontal position (left vs center vs right)
              const hotspotCenter = hotspotRect.left + hotspotRect.width / 2;
              if (hotspotCenter - tooltipWidth / 2 < tileRect.left) {
                position.push('right'); // Too close to left edge, align right
              } else if (hotspotCenter + tooltipWidth / 2 > tileRect.right) {
                position.push('left'); // Too close to right edge, align left
              }

              if (position.length > 0) {
                hotspot.setAttribute('data-position', position.join(' '));
              } else {
                hotspot.removeAttribute('data-position');
              }
            }
          }

          // BFY-LBM: Classe stato visivo
          hotspot.classList.add('bfy-lbm--open');

          // BFY-LBM: ARIA sync
          trigger.setAttribute('aria-expanded', 'true');
          tooltip.setAttribute('aria-hidden', 'false');
        }
      }, 50);
    };

    const hideTooltip = () => {
      hideTimeout = setTimeout(() => {
        this.tooltipManager.closeTooltip(hotspot, tooltip, this.isMobile);

        // BFY-LBM: Rimuovi classe stato
        hotspot.classList.remove('bfy-lbm--open');

        // BFY-LBM: ARIA sync
        trigger.setAttribute('aria-expanded', 'false');
        tooltip.setAttribute('aria-hidden', 'true');
      }, 300);
    };

    // Touch/click events
    if (this.isMobile || 'ontouchstart' in window) {
      let clickLock = false;

      trigger.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        return false;
      });

      trigger.addEventListener('click', (e) => {
        if (clickLock) return;
        clickLock = true;
        setTimeout(() => { clickLock = false; }, 400);

        e.preventDefault();
        e.stopPropagation();

        const isCurrentlyOpen = this.tooltipManager.isOpen(tooltip);
        if (isCurrentlyOpen) {
          hideTooltip();
        } else {
          showTooltip();
        }
      });

    } else {
      // Desktop: hover + focus
      trigger.addEventListener('mouseenter', showTooltip);
      trigger.addEventListener('mouseleave', hideTooltip);
      trigger.addEventListener('focus', showTooltip);
      trigger.addEventListener('blur', hideTooltip);

      // BFY-LBM: Tooltip interaction - cancella auto-close quando utente interagisce
      tooltip.addEventListener('mouseenter', () => {
        clearTimeout(hideTimeout);
        this.tooltipManager.cancelAutoClose();
      });
      tooltip.addEventListener('mouseleave', hideTooltip);
    }

    // Keyboard: Enter/Space
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger.click();
      }
    });
  }

  /**
   * BFY-LBM: Setup interaction hardening - Mobile UX best practices
   * Prevents long-press, copy/paste, drag within the section
   */
  setupInteractionHardening() {
    const root = this.element;

    // Helper: check if target is editable or whitelisted
    const isEditable = (el) =>
      !!el && el.closest('input, textarea, [contenteditable="true"], [data-allow-selection="true"]');

    // Helper: check if target is interactive (links, buttons)
    const isInteractive = (el) =>
      !!el && el.closest('a, button, [role="button"]');

    // Block contextmenu (long-press) - whitelist interactive
    root.addEventListener('contextmenu', (e) => {
      if (!isEditable(e.target) && !isInteractive(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Block copy/cut/paste/selectstart/dragstart
    ['copy', 'cut', 'paste', 'selectstart', 'dragstart'].forEach((evt) => {
      root.addEventListener(evt, (e) => {
        if (!isEditable(e.target)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });

    // Block keyboard shortcuts (Cmd/Ctrl + C/X/V)
    root.addEventListener('keydown', (e) => {
      const k = e.key?.toLowerCase?.();
      if ((e.metaKey || e.ctrlKey) && ['c', 'x', 'v'].includes(k) && !isEditable(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  /**
   * Gestione CTA hotspot con integrazione BeezFy Systems
   */
  async handleHotspotCTA(cta, { productId, variantId, productHandle }) {
    try {
      // Determina il tipo di azione basato sui data attributes
      const isATC = cta.hasAttribute('data-add-to-cart');
      const isWizard = cta.hasAttribute('data-variant-wizard-trigger');

      if (isATC && variantId) {
        // Integrazione diretta con BeezFy ATC System
        if (window.BeezfyATCSystem) {
          this.log('Using BeezFy ATC System for hotspot');

          const result = await window.BeezfyATCSystem.addToCart(variantId, 1);

          if (result.success && this.trackingEnabled) {
            this.trackEvent('hotspot_atc_success', {
              product_id: productId,
              variant_id: variantId
            });
          }
        } else {
          // Fallback to manual cart add
          this.log('Fallback ATC for hotspot');
          await this.fallbackAddToCart(variantId);
        }

      } else if (isWizard && productHandle) {
        // Integrazione con BeezFy Variant Wizard
        if (window.BeezfyVariantWizard) {
          this.log('Opening Variant Wizard for hotspot');

          // Fetch product data
          const productData = await this.fetchProductData(productHandle);

          if (productData) {
            if (this.trackingEnabled) {
              this.trackEvent('hotspot_wizard_open', {
                product_id: productId,
                product_handle: productHandle
              });
            }

            // Open wizard con callback
            window.BeezfyVariantWizard.open(productData, {
              onSelect: (selectedVariant) => {
                if (this.trackingEnabled) {
                  this.trackEvent('hotspot_wizard_select', {
                    product_id: productId,
                    variant_id: selectedVariant.id
                  });
                }
              },
              onClose: () => {
                this.log('Variant wizard closed from hotspot');
              }
            });
          }
        } else {
          // Fallback al product page
          window.location.href = `/products/${productHandle}`;
        }
      } else {
        // Link diretto al prodotto
        window.location.href = cta.href || `/products/${productHandle}`;
      }

    } catch (error) {
      console.error('Hotspot CTA error:', error);

      // Fallback graceful
      if (productHandle) {
        window.location.href = `/products/${productHandle}`;
      }
    }
  }

  /**
   * Fetch product data per Variant Wizard
   */
  async fetchProductData(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const productData = await response.json();

      // Trasforma nel formato richiesto dal Variant Wizard
      return {
        id: productData.id,
        title: productData.title,
        handle: productData.handle,
        options: productData.options,
        variants: productData.variants.map(variant => ({
          id: variant.id,
          title: variant.title,
          available: variant.available,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          options: variant.options,
          featured_image: variant.featured_image
        })),
        images: productData.images
      };

    } catch (error) {
      console.error('Failed to fetch product data:', error);
      return null;
    }
  }

  /**
   * Fallback ATC senza BeezFy System
   */
  async fallbackAddToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // Trigger drawer or popup based on settings
        if (this.settings.atc_action === 'drawer' && window.openDrawer) {
          window.openDrawer();
        } else if (window.BeezfyPopup) {
          // Fetch variant info for popup
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          const addedItem = cartData.items[cartData.items.length - 1];

          window.BeezfyPopup.show({
            product: { title: addedItem.product_title },
            variant: addedItem,
            quantity: 1
          });
        }
      }
    } catch (error) {
      console.error('Fallback ATC failed:', error);
    }
  }

  /**
   * Setup carousel functionality - Enhanced for premium experience
   */
  setupCarousel() {
    if (!this.element.classList.contains('lookbook-layout--carousel')) return;

    const carousel = this.grid;
    if (!carousel) return;

    // Add navigation buttons
    this.addCarouselNavigation(carousel);

    // Touch/swipe support
    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;

    carousel.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
    });

    carousel.addEventListener('mouseleave', () => {
      isDown = false;
    });

    carousel.addEventListener('mouseup', () => {
      isDown = false;
    });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2;
      carousel.scrollLeft = scrollLeft - walk;
    });

    // Touch events
    carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
    });

    carousel.addEventListener('touchmove', (e) => {
      const x = e.touches[0].pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2;
      carousel.scrollLeft = scrollLeft - walk;
    });

    // Scroll snap fallback
    this.setupScrollSnap(carousel);
  }

  /**
   * Add carousel navigation buttons
   */
  addCarouselNavigation(carousel) {
    const nav = document.createElement('div');
    nav.className = 'lookbook-carousel-nav';
    nav.innerHTML = `
      <button class="carousel-nav__prev" aria-label="Previous">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="carousel-nav__next" aria-label="Next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    carousel.parentElement.appendChild(nav);

    const prevBtn = nav.querySelector('.carousel-nav__prev');
    const nextBtn = nav.querySelector('.carousel-nav__next');

    prevBtn.addEventListener('click', () => {
      carousel.scrollBy({ left: -carousel.offsetWidth * 0.8, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      carousel.scrollBy({ left: carousel.offsetWidth * 0.8, behavior: 'smooth' });
    });

    // Update button states
    const updateButtons = () => {
      prevBtn.disabled = carousel.scrollLeft <= 0;
      nextBtn.disabled = carousel.scrollLeft >= carousel.scrollWidth - carousel.offsetWidth;
    };

    carousel.addEventListener('scroll', updateButtons);
    updateButtons();
  }

  /**
   * Setup scroll snap per carousel
   */
  setupScrollSnap(carousel) {
    let isScrolling = false;

    carousel.addEventListener('scroll', () => {
      if (isScrolling) return;

      isScrolling = true;

      requestAnimationFrame(() => {
        const tiles = carousel.querySelectorAll('.lookbook-tile');
        const carouselRect = carousel.getBoundingClientRect();
        const carouselCenter = carouselRect.left + carouselRect.width / 2;

        let closest = null;
        let closestDistance = Infinity;

        tiles.forEach(tile => {
          const tileRect = tile.getBoundingClientRect();
          const tileCenter = tileRect.left + tileRect.width / 2;
          const distance = Math.abs(carouselCenter - tileCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closest = tile;
          }
        });

        if (closest && closestDistance > 50) {
          closest.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }

        isScrolling = false;
      });
    });
  }

  /**
   * Setup video handling
   */
  setupVideos() {
    const videoTiles = this.element.querySelectorAll('.lookbook-tile--video');

    videoTiles.forEach(tile => {
      const video = tile.querySelector('.lookbook-tile__video');
      const playButton = tile.querySelector('.video-play-button');
      const externalVideo = tile.querySelector('.lookbook-tile__external-video');

      if (video) {
        this.setupNativeVideo(video, tile);
      }

      if (playButton && externalVideo) {
        this.setupExternalVideo(playButton, externalVideo, tile);
      }
    });
  }

  /**
   * Setup native video
   */
  setupNativeVideo(video, tile) {
    // Pause video when out of view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && !video.paused) {
          video.pause();
        }
      });
    });

    observer.observe(tile);

    // Click to play/pause
    video.addEventListener('click', () => {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    });
  }

  /**
   * Setup external video
   */
  setupExternalVideo(playButton, container, tile) {
    const videoUrl = container.dataset.videoUrl;

    playButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (this.trackingEnabled) {
        this.trackEvent('video_play', {
          video_url: videoUrl,
          tile_id: tile.dataset.tileId
        });
      }

      this.openVideoModal(videoUrl);
    });
  }

  /**
   * Apri video in modal
   */
  openVideoModal(videoUrl) {
    // Create modal
    const modal = document.createElement('div');
    modal.className = 'lookbook-video-modal';
    modal.innerHTML = `
      <div class="video-modal__overlay"></div>
      <div class="video-modal__container">
        <button class="video-modal__close" aria-label="Close video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="video-modal__content">
          ${this.getVideoEmbedCode(videoUrl)}
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close handlers
    const closeModal = () => {
      modal.remove();
      document.body.style.overflow = '';
    };

    modal.querySelector('.video-modal__close').addEventListener('click', closeModal);
    modal.querySelector('.video-modal__overlay').addEventListener('click', closeModal);

    // ESC key
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEsc);
      }
    };
    document.addEventListener('keydown', handleEsc);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Animate in
    requestAnimationFrame(() => {
      modal.style.opacity = '1';
    });
  }

  /**
   * Generate video embed code
   */
  getVideoEmbedCode(url) {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = this.extractYouTubeId(url);
      return `<iframe src="https://www.youtube.com/embed/${videoId}?autoplay=1" frameborder="0" allowfullscreen></iframe>`;
    }

    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = this.extractVimeoId(url);
      return `<iframe src="https://player.vimeo.com/video/${videoId}?autoplay=1" frameborder="0" allowfullscreen></iframe>`;
    }

    // Direct video URL
    return `<video controls autoplay><source src="${url}"></video>`;
  }

  /**
   * Extract YouTube video ID
   */
  extractYouTubeId(url) {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : '';
  }

  /**
   * Extract Vimeo video ID
   */
  extractVimeoId(url) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
  }

  /**
   * Setup lightbox functionality - Premium gallery experience
   */
  setupLightbox() {
    // Create lightbox for shop the look tiles
    const shopTheLookTiles = this.element.querySelectorAll('.lookbook-tile--shop_the_look');

    shopTheLookTiles.forEach(tile => {
      const image = tile.querySelector('.lookbook-tile__image');
      if (image) {
        image.addEventListener('click', (e) => {
          if (!e.target.closest('.lookbook-hotspot')) {
            this.openLightbox(image.src, tile);
          }
        });
      }
    });
  }

  /**
   * Open image in lightbox
   */
  openLightbox(imageSrc, tile) {
    const lightbox = document.createElement('div');
    lightbox.className = 'lookbook-lightbox';
    lightbox.innerHTML = `
      <div class="lightbox__overlay"></div>
      <div class="lightbox__container">
        <button class="lightbox__close" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <img src="${imageSrc}" alt="" class="lightbox__image">
      </div>
    `;

    document.body.appendChild(lightbox);
    document.body.style.overflow = 'hidden';

    // Clone hotspots to lightbox
    const hotspots = tile.querySelector('.lookbook-hotspots');
    if (hotspots) {
      const lightboxContainer = lightbox.querySelector('.lightbox__container');
      const hotspotsClone = hotspots.cloneNode(true);
      lightboxContainer.appendChild(hotspotsClone);

      // Re-bind hotspot events
      hotspotsClone.querySelectorAll('.lookbook-hotspot').forEach(hotspot => {
        this.setupHotspotTooltip(hotspot);
      });
    }

    // Close handlers
    const closeLightbox = () => {
      lightbox.remove();
      document.body.style.overflow = '';
    };

    lightbox.querySelector('.lightbox__close').addEventListener('click', closeLightbox);
    lightbox.querySelector('.lightbox__overlay').addEventListener('click', closeLightbox);

    // ESC key
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
        document.removeEventListener('keydown', handleEsc);
      }
    };
    document.addEventListener('keydown', handleEsc);

    // Animate in
    requestAnimationFrame(() => {
      lightbox.classList.add('is-visible');
    });
  }

  /**
   * Setup lazy loading
   */
  setupLazyLoading(tile) {
    const images = tile.querySelectorAll('img[loading="lazy"]');

    if (!window.IntersectionObserver) {
      // Fallback: load immediately
      images.forEach(img => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
      return;
    }

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  /**
   * Setup accessibility
   */
  setupAccessibility() {
    // Ensure proper ARIA labels
    this.hotspots.forEach((hotspot, index) => {
      if (!hotspot.hasAttribute('aria-label')) {
        const productTitle = hotspot.querySelector('.hotspot-card__title')?.textContent || 'Product';
        hotspot.setAttribute('aria-label', `View ${productTitle}`);
      }

      // Ensure focusable
      if (!hotspot.hasAttribute('tabindex')) {
        hotspot.setAttribute('tabindex', '0');
      }
    });

    // Keyboard navigation for carousel
    if (this.element.classList.contains('lookbook-layout--carousel')) {
      this.setupCarouselKeyboardNav();
    }
  }

  /**
   * Setup keyboard navigation for carousel
   */
  setupCarouselKeyboardNav() {
    const carousel = this.grid;
    if (!carousel) return;

    carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        carousel.scrollBy({ left: -300, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        carousel.scrollBy({ left: 300, behavior: 'smooth' });
      }
    });
  }

  /**
   * Setup analytics tracking
   */
  setupAnalytics() {
    // Track section visibility
    if (window.IntersectionObserver) {
      const analyticsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasBeenViewed) {
            this.trackEvent('section_view', {
              section_id: this.sectionId,
              layout_type: this.layoutType,
              campaign: this.campaignTag
            });
            this.hasBeenViewed = true;
            analyticsObserver.unobserve(this.element);
          }
        });
      }, { threshold: 0.1 });

      analyticsObserver.observe(this.element);
    }
  }

  /**
   * Track analytics event
   */
  trackEvent(eventName, data = {}) {
    if (!this.trackingEnabled) return;

    const eventData = {
      event: `beezfy_lookbook_${eventName}`,
      section_id: this.sectionId,
      layout_type: this.layoutType,
      timestamp: new Date().toISOString(),
      ...data
    };

    // Send to BeezFy analytics if available
    if (window.beezfyAnalytics) {
      window.beezfyAnalytics.track(eventData);
    }

    // Send to Google Analytics if available
    if (window.gtag) {
      window.gtag('event', eventName, {
        custom_parameter_section_id: this.sectionId,
        custom_parameter_layout: this.layoutType,
        ...data
      });
    }

    // Send to Facebook Pixel if available
    if (window.fbq) {
      window.fbq('trackCustom', `LookbookSection${eventName}`, eventData);
    }

    this.log('Event tracked:', eventName, eventData);
  }

  /**
   * Handle first view (for analytics)
   */
  handleFirstView() {
    this.hasBeenViewed = true;

    if (this.trackingEnabled) {
      this.trackEvent('first_view', {
        tiles_count: this.tiles.length,
        hotspots_count: this.hotspots.length
      });
    }
  }

  /**
   * Handle resize
   */
  handleResize() {
    // Recalculate masonry if needed
    if (this.element.classList.contains('lookbook-layout--masonry')) {
      this.recalculateMasonry();
    }

    // Update carousel scroll position
    if (this.element.classList.contains('lookbook-layout--carousel')) {
      this.updateCarouselPosition();
    }
  }

  /**
   * Recalculate masonry layout - Enhanced for premium layouts
   */
  recalculateMasonry() {
    // Force reflow for masonry
    const grid = this.grid;
    if (grid) {
      // Apply premium masonry animation
      const tiles = grid.querySelectorAll('.lookbook-tile');
      tiles.forEach((tile, index) => {
        tile.style.animationDelay = `${index * 50}ms`;
        tile.classList.add('masonry-reflow');
      });

      // Cleanup after animation
      setTimeout(() => {
        tiles.forEach(tile => {
          tile.classList.remove('masonry-reflow');
          tile.style.animationDelay = '';
        });
      }, 1000);

      grid.style.columns = getComputedStyle(grid).columns;
    }
  }

  /**
   * Update carousel position
   */
  updateCarouselPosition() {
    // Maintain scroll position on resize
    const carousel = this.grid;
    if (carousel && carousel.scrollLeft > 0) {
      // Smooth adjustment
      requestAnimationFrame(() => {
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        if (carousel.scrollLeft > maxScroll) {
          carousel.scrollLeft = maxScroll;
        }
      });
    }
  }

  /**
   * BFY-LBM: Destroy instance (Theme Editor cleanup)
   */
  destroy() {
    // BFY-LBM: Unbind global click-outside listener
    if (this.boundListeners.onDocClick) {
      document.removeEventListener('click', this.boundListeners.onDocClick, true);
      this.boundListeners.onDocClick = null;
    }

    // Clean up observers
    if (this.observer) {
      this.observer.disconnect();
    }

    // BFY-LBM: Close all tooltips
    this.tooltipManager.closeAll(this.isMobile);

    this.log('Section destroyed:', this.sectionId);
  }

  /**
   * Logging helper
   */
  log(...args) {
    if (window.beezfyLookbook?.isDebug) {
    }
  }
}

/**
 * Auto-initialize when DOM is ready
 */
// Wait for BeezFy core systems
const initWhenReady = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
    return;
  }

  // Initialize global instance
  window.BeezfyLookbookMosaic = BeezfyLookbookMosaic;
  window.beezfyLookbook = new BeezfyLookbookMosaic();
};

initWhenReady();

/**
 * BFY-LBM: Theme Editor lifecycle events
 */
document.addEventListener('shopify:section:load', (e) => {
  const target = e.target || (e.detail?.sectionId && document.querySelector(`[data-section-id="${e.detail.sectionId}"]`));

  if (target?.dataset.sectionType === 'beezfy-lookbook-mosaic') {
    const sectionInstance = new LookbookSection(target, window.beezfySettings || {});

    // Store reference for unload
    if (!window.__bfyLbmInstances) {
      window.__bfyLbmInstances = new Map();
    }
    window.__bfyLbmInstances.set(target.dataset.sectionId, sectionInstance);
  }
});

document.addEventListener('shopify:section:unload', (e) => {
  const sectionId = e.detail?.sectionId;

  if (sectionId && window.__bfyLbmInstances?.has(sectionId)) {
    const instance = window.__bfyLbmInstances.get(sectionId);
    instance.destroy();
    window.__bfyLbmInstances.delete(sectionId);
  }
});

/**
 * CSS for video modal (injected dynamically)
 */
const videoModalCSS = `
  .lookbook-video-modal {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .video-modal__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.9);
    cursor: pointer;
  }

  .video-modal__container {
    position: relative;
    width: 90vw;
    max-width: 1200px;
    aspect-ratio: 16 / 9;
    background: black;
    border-radius: 8px;
    overflow: hidden;
  }

  .video-modal__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
  }

  .video-modal__close:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  .video-modal__content {
    width: 100%;
    height: 100%;
  }

  .video-modal__content iframe,
  .video-modal__content video {
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    .video-modal__container {
      width: 95vw;
      aspect-ratio: 16 / 9;
    }
  }
`;

// Inject CSS
if (!document.getElementById('beezfy-lookbook-video-modal-css')) {
  const style = document.createElement('style');
  style.id = 'beezfy-lookbook-video-modal-css';
  style.textContent = videoModalCSS;
  document.head.appendChild(style);
}

/**
 * Performance and Memory Management Utilities
 */
class LookbookPerformanceManager {
  constructor() {
    this.instances = new Map();
    this.raf = null;
    this.resizeTimeout = null;
    this.bindEvents();
  }

  /**
   * Register a lookbook instance for performance monitoring
   */
  register(instance) {
    this.instances.set(instance.sectionId, instance);
  }

  /**
   * Unregister a lookbook instance
   */
  unregister(sectionId) {
    this.instances.delete(sectionId);
  }

  /**
   * Bind global performance events
   */
  bindEvents() {
    // Throttled resize handler
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleGlobalResize();
      }, 100);
    });

    // Visibility change handler
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange();
    });

    // Memory cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });

    // Intersection observer for performance monitoring
    if (window.IntersectionObserver) {
      this.setupPerformanceObserver();
    }
  }

  /**
   * Handle global resize for all instances
   */
  handleGlobalResize() {
    if (this.raf) cancelAnimationFrame(this.raf);

    this.raf = requestAnimationFrame(() => {
      this.instances.forEach(instance => {
        if (instance.handleResize) {
          instance.handleResize();
        }
      });
    });
  }

  /**
   * Handle visibility change for performance optimization
   */
  handleVisibilityChange() {
    const isHidden = document.hidden;

    this.instances.forEach(instance => {
      if (isHidden) {
        // Pause animations and heavy operations
        instance.element?.style.setProperty('--animation-play-state', 'paused');
      } else {
        // Resume animations
        instance.element?.style.setProperty('--animation-play-state', 'running');
      }
    });
  }

  /**
   * Setup performance observer for Core Web Vitals
   */
  setupPerformanceObserver() {
    if (!window.PerformanceObserver) return;

    // LCP observer
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.url && entry.url.includes('lookbook')) {
          }
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // Observer not supported
    }

    // Layout shift observer
    try {
      const clsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (!entry.hadRecentInput) {
            const sources = entry.sources || [];
            sources.forEach(source => {
              if (source.node?.closest?.('.beezfy-lookbook-mosaic')) {
              }
            });
          }
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      // Observer not supported
    }
  }

  /**
   * Memory cleanup
   */
  cleanup() {
    this.instances.forEach(instance => {
      if (instance.destroy) {
        instance.destroy();
      }
    });
    this.instances.clear();

    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}

/**
 * Initialize performance manager
 */
window.lookbookPerformanceManager = new LookbookPerformanceManager();

/**
 * Enhanced error boundary and logging
 */
window.addEventListener('error', (event) => {
  if (event.filename && event.filename.includes('beezfy-lookbook-mosaic')) {
    console.error('Lookbook error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });

    // Send to analytics if available
    if (window.beezfyAnalytics) {
      window.beezfyAnalytics.track({
        event: 'beezfy_lookbook_error',
        error_message: event.message,
        error_filename: event.filename,
        error_line: event.lineno
      });
    }
  }
});

/**
 * Progressive enhancement checker
 */
const checkFeatureSupport = () => {
  const features = {
    intersectionObserver: 'IntersectionObserver' in window,
    customProperties: CSS.supports('--custom-property', 'value'),
    gridLayout: CSS.supports('display', 'grid'),
    flexboxGap: CSS.supports('gap', '1rem'),
    backdropFilter: CSS.supports('backdrop-filter', 'blur(10px)'),
    contentVisibility: CSS.supports('content-visibility', 'auto')
  };


  // Apply fallbacks for unsupported features
  if (!features.gridLayout) {
    document.body.classList.add('no-css-grid');
  }

  if (!features.intersectionObserver) {
    document.body.classList.add('no-intersection-observer');
  }

  return features;
};

// Check support when script loads
checkFeatureSupport();

})(); // Fine IIFE

/**
 * ============================================
 * SHOW ALL PRODUCTS BUTTON - PREMIUM SYSTEM
 * ============================================
 */

class BeezfyShowAllProducts {
  constructor(tileElement, products) {
    this.tile = tileElement;
    this.products = products;
    this.button = null;
    this.overlay = null;
    this.init();
  }

  init() {
    // Crea bottone solo se ci sono prodotti
    if (!this.products || this.products.length === 0) return;

    // Check se bottone è abilitato
    const showButton = this.tile.dataset.showAllButton;
    if (showButton === 'false') return;

    this.createButton();
    this.createOverlay();
    this.attachEvents();
  }

  createButton() {
    const button = document.createElement('button');
    button.className = 'bzfy-show-all-products';
    button.type = 'button';
    button.setAttribute('aria-label', 'Mostra tutti i prodotti');

    // Get custom text from settings
    const customText = this.tile.dataset.showAllText || 'Mostra {count} prodotti';
    const buttonText = customText
      .replace('{count}', this.products.length)
      .replace('{products}', this.products.length === 1 ? 'prodotto' : 'prodotti');

    // Get position from settings
    const position = this.tile.dataset.showAllPosition || 'bottom-center';
    button.setAttribute('data-position', position);

    button.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="9" cy="9" r="2"/>
        <circle cx="15" cy="9" r="2"/>
        <circle cx="9" cy="15" r="2"/>
        <circle cx="15" cy="15" r="2"/>
      </svg>
      <span>${buttonText}</span>
    `;

    this.tile.appendChild(button);
    this.button = button;
  }

  async createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'bzfy-all-products-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'bzfy-products-title');

    // Get logo from header
    const headerLogo = document.querySelector('.header-logo img, .site-header__logo img, header img[alt*="logo" i]');
    const logoHTML = headerLogo
      ? `<img src="${headerLogo.src}" alt="${headerLogo.alt || 'Logo'}" class="bzfy-modal-logo" loading="eager">`
      : `<div class="bzfy-modal-logo-text">${document.querySelector('.header-logo a, .site-header__logo a')?.textContent || 'Shop'}</div>`;

    overlay.innerHTML = `
      <div class="bzfy-products-grid-container">
        <div class="bzfy-products-grid-header">
          <div class="bzfy-modal-header-content">
            ${logoHTML}
            <h3 id="bzfy-products-title">Shop the Look</h3>
          </div>
          <button type="button" class="bzfy-close-products" aria-label="Chiudi">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="bzfy-products-grid">
          <div class="bzfy-loading">Loading products...</div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.overlay = overlay;

    // Load product cards using beezfy-product-card-v3
    await this.loadProductCards();
  }

  async loadProductCards() {
    const grid = this.overlay.querySelector('.bzfy-products-grid');

    try {
      // Usa il fallback createProductCard che renderizza card pulite
      // Questo evita di caricare 141 elementi DOM del template completo
      grid.innerHTML = this.products.map(product => this.createProductCard(product)).join('');
    } catch (error) {
      console.error('Error loading product cards:', error);
      grid.innerHTML = '<p>Errore nel caricamento dei prodotti</p>';
    }
  }

  createProductCard(product) {
    // Fallback basic card with high-quality images
    const price = product.price ? this.formatMoney(product.price) : '';
    const comparePrice = product.compare_at_price && product.compare_at_price > product.price
      ? this.formatMoney(product.compare_at_price)
      : '';

    // Generate responsive image URLs with Shopify CDN width parameter
    // Shopify CDN format: image.jpg?v=xxx&width=600
    const getImageUrl = (width) => {
      if (!product.image) return '';

      // Remove any existing width parameter first
      let imageUrl = product.image.replace(/[&?]width=\d+/g, '');

      // Add new width parameter
      if (imageUrl.includes('?')) {
        return `${imageUrl}&width=${width}`;
      }
      return `${imageUrl}?width=${width}`;
    };

    // High-quality srcset for retina displays
    const srcset = `
      ${getImageUrl(300)} 300w,
      ${getImageUrl(600)} 600w,
      ${getImageUrl(900)} 900w,
      ${getImageUrl(1200)} 1200w
    `.trim();

    // Use 600px as default (good for mobile 2x retina)
    const defaultSrc = getImageUrl(600);

    return `
      <a href="${product.url}" class="bzfy-product-item">
        <img
          src="${defaultSrc}"
          srcset="${srcset}"
          sizes="(max-width: 749px) 50vw, 300px"
          alt="${this.escapeHtml(product.title)}"
          class="bzfy-product-item__image"
          loading="lazy"
        />
        <h4 class="bzfy-product-item__title">${this.escapeHtml(product.title)}</h4>
        <p class="bzfy-product-item__price">
          ${comparePrice ? `<span class="bzfy-product-item__compare">${comparePrice}</span>` : ''}
          ${price}
        </p>
      </a>
    `;
  }

  attachEvents() {
    // Open overlay
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openOverlay();
    });

    // Close button
    const closeBtn = this.overlay.querySelector('.bzfy-close-products');
    closeBtn.addEventListener('click', () => this.closeOverlay());

    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeOverlay();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('bzfy-active')) {
        this.closeOverlay();
      }
    });
  }

  openOverlay() {
    this.overlay.classList.add('bzfy-active');
    document.body.style.overflow = 'hidden';

    // Focus trap
    const focusableElements = this.overlay.querySelectorAll(
      'button, a, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  closeOverlay() {
    this.overlay.classList.remove('bzfy-active');
    document.body.style.overflow = '';
    this.button.focus(); // Return focus to button
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize Show All Products for each shop_the_look tile
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.lookbook-tile__shop-look').forEach(tile => {
    // Get all hotspots products
    const hotspots = tile.querySelectorAll('.lookbook-hotspot');
    const products = Array.from(hotspots).map(hotspot => {
      const productId = hotspot.dataset.productId;
      const productHandle = hotspot.dataset.productHandle;
      const card = hotspot.querySelector('.hotspot-card');

      if (!card) return null;

      const image = card.querySelector('.hotspot-card__image img');
      const title = card.querySelector('.hotspot-card__title');
      const priceEl = card.querySelector('.price');
      const comparePriceEl = card.querySelector('.price-compare');

      return {
        id: productId,
        handle: productHandle,
        url: `/products/${productHandle}`,
        title: title ? title.textContent.trim() : '',
        image: image ? image.src : '',
        price: priceEl ? parseMoney(priceEl.textContent) : 0,
        compare_at_price: comparePriceEl ? parseMoney(comparePriceEl.textContent) : 0
      };
    }).filter(p => p !== null);

    if (products.length > 0) {
      // Check if button is enabled from section settings
      const blockId = tile.dataset.blockId;
      const blockElement = document.querySelector(`[data-tile-id="${blockId}"]`);

      // For now, always enable. You can add data attribute check later
      new BeezfyShowAllProducts(tile, products);
    }
  });
});

// Helper function to parse money strings
function parseMoney(str) {
  if (!str) return 0;
  // Remove currency symbols and spaces, replace comma with dot
  const cleaned = str.replace(/[^0-9,.-]/g, '').replace(',', '.');
  return parseFloat(cleaned) * 100; // Convert to cents
}